import React, { useMemo } from 'react'
import clsx from 'clsx'
import {
  DAY_START_HOUR,
  DAY_END_HOUR,
  ROW_PAD,
  LANE_GAP,
  BLOCK_H,
  parseNaiveDate,
  toDateKey,
  dailyBlockStyle,
  getJobStatusColorClass,
  assignLanes,
  formatClock,
} from '../allocationUtils'

/**
 * DailyTimelineRow
 * ----------------
 * Renders an employee's row on the Daily Timeline Gantt board.
 * Shows hour grid background lines, current time marker, and time-positioned job pills.
 */
export default function DailyTimelineRow({
  employee,
  blocks = [],
  jobBlocks = [],
  dateStr,
  hours = [],
  slotWidth = 120,
  rowH,
  blinkTarget,
  onDrop,
  onSlotDrop,
  onSlotClick,
  onJobClick,
  draggedJob,
}) {
  const isBlink = blinkTarget?.employeeId === employee.employee_id
  const isToday = toDateKey(new Date()) === dateStr

  // Current time position calculation
  const now = new Date()
  const currentHourFloat = now.getHours() + now.getMinutes() / 60
  const isNowInWindow = isToday && currentHourFloat >= DAY_START_HOUR && currentHourFloat <= DAY_END_HOUR
  const nowLeft = isNowInWindow ? (currentHourFloat - DAY_START_HOUR) * slotWidth : null

  // Use pre-assigned blocks (which include both jobs and available slots) or compute fallback
  const displayBlocks = useMemo(() => {
    if (blocks && blocks.length > 0) return blocks
    const raw = (jobBlocks || []).map((b) => {
      const s = parseNaiveDate(b.planned_start)
      const e = b.planned_end ? parseNaiveDate(b.planned_end) : new Date(s.getTime() + 60 * 60 * 1000)
      return {
        ...b,
        start: s,
        end: e,
        kind: 'job',
      }
    })
    return assignLanes(raw).blocks
  }, [blocks, jobBlocks])

  const totalWidth = hours.length * slotWidth

  return (
    <div
      className={clsx(
        'relative border-b border-outline-variant/30 transition-colors group/row',
        isBlink && 'daily-row-blink bg-primary/10'
      )}
      style={{
        height: `${rowH}px`,
        width: `${totalWidth}px`,
        minWidth: `${totalWidth}px`,
      }}
      onDragOver={(e) => {
        if (draggedJob) {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'copy'
        }
      }}
      onDrop={(e) => {
        e.preventDefault()
        if (onDrop) onDrop(employee)
      }}
    >
      {/* Hour grid lines */}
      {hours.map(({ hour }) => {
        const left = (hour - DAY_START_HOUR) * slotWidth
        return (
          <div
            key={hour}
            className="absolute top-0 bottom-0 pointer-events-none border-r border-outline-variant/15"
            style={{ left: `${left + slotWidth}px` }}
          />
        )
      })}

      {/* Real-time "now" vertical marker */}
      {nowLeft !== null && (
        <div
          className="absolute top-0 bottom-0 z-20 pointer-events-none flex flex-col items-center"
          style={{ left: `${nowLeft}px` }}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-red-500 shadow-xs -mt-0.5" />
          <div className="w-px h-full bg-red-500/70" />
        </div>
      )}

      {/* Empty slot guide or drag cue when empty */}
      {displayBlocks.length === 0 && !draggedJob && (
        <div className="absolute inset-0 flex items-center justify-start pl-8 text-xs text-outline/35 italic select-none pointer-events-none">
          No committed jobs or available capacity on this day
        </div>
      )}

      {/* Drop overlay hint when dragging a job over this row */}
      {draggedJob && (
        <div className="absolute inset-x-2 inset-y-1 rounded-xl border-2 border-dashed border-primary/50 bg-primary/5 pointer-events-none flex items-center justify-center z-10">
          <span className="text-xs font-semibold text-primary px-3 py-1 bg-surface rounded-full shadow-xs">
            Drop to assign "{draggedJob.job_name || draggedJob.title || 'Job'}" to {employee.name}
          </span>
        </div>
      )}

      {/* Job pills & Available slot pills */}
      {displayBlocks.map((b) => {
        const style = dailyBlockStyle(dateStr, b.planned_start, b.planned_end, b.planned_hours, employee?.shift_intime)
        if (!style) return null

        const top = ROW_PAD + (b.lane || 0) * LANE_GAP

        if (b.kind === 'slot') {
          return (
            <div
              key={b.id || `slot-${b.startTime}-${b.endTime}`}
              onClick={() => onSlotClick && onSlotClick(employee, b)}
              onDragOver={(e) => {
                if (draggedJob) {
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'copy'
                }
              }}
              onDrop={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (onSlotDrop) onSlotDrop(employee, b)
                else if (onDrop) onDrop(employee, b)
              }}
              className={clsx(
                'absolute rounded-xl px-2.5 py-1 flex items-center justify-between cursor-pointer select-none',
                'border-2 border-dashed border-primary/50 text-primary bg-primary/5 hover:bg-primary/10 hover:border-primary',
                'shadow-xs hover:shadow-md transition-all duration-150 z-10 group'
              )}
              style={{
                ...style,
                top: `${top}px`,
                height: `${BLOCK_H}px`,
              }}
              title={`Available Slot: ${formatClock(b.planned_start)} – ${formatClock(b.planned_end)} (${b.free_hours}h free)${b.shift_name ? ` • ${b.shift_name}` : ''}\nClick or drop a job to allocate`}
            >
              <div className="flex flex-col min-w-0 pr-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-extrabold tracking-wider uppercase truncate text-primary leading-tight">
                    Available Slot
                  </span>
                  <span className="text-[9px] font-bold text-primary bg-primary/15 px-1.5 py-0.2 rounded leading-tight">
                    {b.free_hours}h free
                  </span>
                </div>
                <span className="text-[9px] font-medium text-primary/75 truncate leading-none mt-0.5">
                  {formatClock(b.planned_start)} – {formatClock(b.planned_end)}
                  {b.shift_name ? ` • ${b.shift_name}` : ''}
                </span>
              </div>
              <span className="material-symbols-outlined text-sm text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                add_task
              </span>
            </div>
          )
        }

        const statusClass = getJobStatusColorClass(b.status)
        const jobTitle = b.job_name || b.title || `Job #${b.job_id}`
        const startTimeStr = formatClock(b.planned_start)
        const endTimeStr = formatClock(b.planned_end)

        return (
          <div
            key={b.job_id || b.id}
            onClick={() => onJobClick && onJobClick(b, employee)}
            className={clsx(
              'absolute rounded-xl px-2.5 py-1 flex items-center justify-between cursor-pointer select-none',
              'shadow-xs hover:shadow-md hover:scale-[1.01] active:scale-[0.99] transition-all duration-150 z-10',
              statusClass
            )}
            style={{
              ...style,
              top: `${top}px`,
              height: `${BLOCK_H}px`,
            }}
            title={`${jobTitle}\nTime: ${startTimeStr} – ${endTimeStr}\nHours: ${b.planned_hours || '--'}h\nStatus: ${b.status || 'Active'}`}
          >
            <div className="flex items-center gap-1.5 min-w-0 pr-1">
              <span className="text-[11px] font-bold tracking-tight truncate max-w-[180px]">
                {jobTitle}
              </span>
              {b.overrun_flag && (
                <span
                  className="px-1 py-0.2 text-[9px] font-bold bg-amber-400 text-amber-950 rounded-md shrink-0"
                  title="Overrun risk flag"
                >
                  !
                </span>
              )}
            </div>

            <div className="flex items-center gap-1 shrink-0 text-[10px] font-medium opacity-90">
              <span className="hidden sm:inline opacity-75">{startTimeStr}</span>
              <span className="bg-black/20 px-1.5 py-0.5 rounded-md font-semibold text-[10px]">
                {b.planned_hours ? `${b.planned_hours}h` : '1h'}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}
